import trashIcon from '@SvgIcon/trash-b.svg'

import { Dashboard } from '@uppy/vue'
import Uppy from '@uppy/core'
import AwsS3 from '@uppy/aws-s3'
import ImageEditor from '@uppy/image-editor'
import svgIconHtml from '../../svgIconHtml'

/**
 * Import styles:
 * <style src="@uppy/core/dist/style.css"></style>
 * <style src="@uppy/dashboard/dist/style.css"></style>
 */

export default {
  name: 'UiFormUppy',

  components: {
    UppyDashboard: Dashboard,
  },

  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },

  inject: {
    ngForm: {
      default: '',
    },
  },

  // language=HTML (for PhpStorm)
  template: `
    <div class="ui-input">
      <input
        v-if="formFieldName"
        type="hidden"
        :name="formFieldName"
        :value="valueAsJson"
      />

      <div
        v-if="valueArray.length && showFilenames"
        class="warning callout"
        style="font-size: 14px; color: #000;"
      >
        <b>Files uploaded:</b>
        <ul style="margin-top: 0; margin-bottom: 0;">
          <li v-for="(val, idx) in valueArray" :key="idx">
            {{ val }}
            <button
              type="button"
              class="tiny hollow alert button"
              aria-label="Remove"
              @click="removeFile(val)"
              style="margin: 0"
            >
              ${svgIconHtml(trashIcon)}
            </button>
          </li>
        </ul>
      </div>

      <uppy-dashboard
        :uppy="uppy"
        :plugins="['ImageEditor']"
        :props="dashboardProps"
      />
    </div>
  `,

  props: {
    modelValue: {
      type: Array,
      default() {
        return []
      },
    },
    paramsUrl: {
      type: String,
      required: true,
    },
    formFieldName: {
      type: String,
    },
    allowMultiple: {
      type: Boolean,
      default: false,
    },
    minNumFiles: {
      type: Number,
      default: 0,
    },
    maxNumFiles: {
      type: Number,
    },
    allowedTypes: {
      type: Array,
    },
    autoProceed: {
      type: Boolean,
      default: false,
    },
    showFilenames: {
      type: Boolean,
      default: true,
    },
    // bytes = {X}MB * 1E+6         (1E+6 === 1,000,000)
    maxFileSize: {
      type: Number,
      default: 50 * 1000000,
    },
  },

  data() {
    return {
      uppy: null,
    }
  },

  computed: {
    valueArray() {
      if (Array.isArray(this.modelValue)) {
        return this.modelValue
      }
      if (this.modelValue) {
        return [this.modelValue]
      }
      return []
    },

    valueAsJson() {
      return JSON.stringify(this.valueArray)
    },

    shouldShowUploader() {
      if (!this.allowMultiple && this.valueArray.length > 0) {
        return false
      }
      return true
    },

    dashboardProps() {
      const browseText = this.allowMultiple
        ? 'Drop files here, paste, or %{browse}'
        : 'Drop file here, paste, or %{browse}'

      return {
        theme: 'light',
        doneButtonHandler: null,
        showRemoveButtonAfterComplete: true,
        proudlyDisplayPoweredByUppy: false,
        locale: {
          strings: {
            dropPasteFiles: browseText,
          },
        },
      }
    },
  },

  methods: {
    removeFile(val) {
      let values = this.valueArray.slice()
      const idx = values.indexOf(val)
      if (idx !== -1) {
        values.splice(idx, 1)
        this.modelValue = values
        this.$emit('update:modelValue', values)
      }
    },
  },

  beforeDestroy() {
    this.uppy.close()
  },

  created() {
    const uppy = new Uppy({
      debug: true,
      autoProceed: this.autoProceed,
      allowMultipleUploads: this.maxNumFiles !== 1,
      restrictions: {
        maxFileSize: this.maxFileSize,
        maxNumberOfFiles: this.maxNumFiles,
        minNumberOfFiles: this.minNumFiles,
        allowedFileTypes: this.allowedTypes,
      },
    })
      .use(ImageEditor, {
        quality: 0.8,
      })
      .use(AwsS3, {
        getUploadParameters: (file) => {
          return fetch(this.paramsUrl, {
            method: 'post',
            headers: {
              accept: 'application/json',
              'content-type': 'application/json',
            },
            body: JSON.stringify({
              filename: file.name,
              contentType: file.type,
            }),
          })
            .then(response => response.json())
            .then((data) => {
              if ('error' in data) {
                alert(data.error)
                return false
              }
              return {
                method: data.method,
                url: data.url,
                fields: data.fields,
              }
            })
        },
      })

    uppy.on('file-removed', (file, reason) => {
      if (reason === 'removed-by-user') {
        const values = this.valueArray.slice()
        const idx = values.indexOf(file.meta.filename)
        if (idx !== -1) {
          values.splice(idx, 1)
        }
        this.$emit('update:modelValue', values)
      }
    })

    uppy.on('upload-success', (file) => {
      let values = this.valueArray.slice()
      if (!this.allowMultiple) {
        values = [file.meta.filename]
      } else {
        values.push(file.meta.filename)
      }
      this.$emit('update:modelValue', values)
    })

    this.uppy = uppy
  },
}
